import { Typography } from '@material-ui/core';
import { graphql, Link } from 'gatsby';
import {
  ArrowRight,
  BarChart3,
  Check,
  Database,
  Globe,
  Sparkles,
  Target
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { ContactForm } from '../components/ContactForm';
import { DemoButton } from '../components/DemoButton';
import { Headline } from '../components/Headline';
import {
  Reveal,
  useInView,
  usePrefersReducedMotion
} from '../components/motion';
import { ProductIcon } from '../components/ProductIcon';
import { SEO } from '../components/SEO';
import MainLayout from '../layouts/main';
import styled from '../styled';
import { COLORS } from '../themes/color';
import { Integration } from '../types/Integration';

const INK = COLORS.INK;

const IntegrationsHero = styled('div')`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 20px;
  padding: 72px 24px 0;

  @media (max-width: 800px) {
    padding-top: 48px;
    margin-bottom: 16px;
  }
`;

const NetworksSection = styled('section')`
  position: relative;
  background: linear-gradient(
    180deg,
    #fff 0%,
    #f4f8fc 10%,
    #f4f8fc 90%,
    #fff 100%
  );
  background-color: #f4f8fc;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
      rgba(14, 28, 43, 0.06) 1px,
      transparent 1px
    );
    background-size: 24px 24px;
    mask-image: radial-gradient(
      ellipse 75% 65% at 50% 40%,
      black 30%,
      transparent 100%
    );
    pointer-events: none;
  }

  > * {
    position: relative;
  }
`;

const NetworksInner = styled('div')`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px 80px;

  @media (max-width: 800px) {
    padding-bottom: 56px;
  }
`;

const NetworksHeader = styled('div')`
  text-align: center;
  margin: 0 auto 20px;
`;

const SyncBadge = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 16px;
  border: 1px solid #d4e0ff;
  border-radius: 100px;
  background: #fff;
  font-size: 11.5px;
  letter-spacing: 0.08em;
  color: ${INK};
  white-space: nowrap;
  box-shadow: 0 4px 16px rgba(46, 92, 255, 0.12);

  @keyframes syncPulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.35;
    }
  }

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #52c41a;
    animation: syncPulse 2.4s ease-in-out infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
`;

const MosaicGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: repeat(3, 1fr);

    /* Keep the mosaic short on small screens. */
    > div:nth-of-type(n + 19) {
      display: none;
    }
  }
`;

const MosaicTileFrame = styled('div')`
  position: relative;
  height: 104px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(14, 28, 43, 0.04);
  overflow: hidden;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(46, 92, 255, 0.1);
    transform: translateY(-2px);
  }

  @keyframes mosaicIn {
    from {
      opacity: 0;
      transform: scale(0.92);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  @keyframes mosaicOut {
    from {
      opacity: 1;
      transform: none;
    }
    to {
      opacity: 0;
      transform: scale(0.92);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const MosaicLayer = styled('div')`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 10px 10px;
  box-sizing: border-box;

  &.in {
    animation: mosaicIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  &.out {
    animation: mosaicOut 0.5s ease both;
    pointer-events: none;
  }

  @media (prefers-reduced-motion: reduce) {
    &.in,
    &.out {
      animation: none;
    }

    &.out {
      display: none;
    }
  }
`;

const LogoImg = styled('img')`
  max-height: 26px;
  max-width: 75%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
`;

const LogoName = styled('h3')`
  font-size: 11px;
  font-weight: normal;
  color: #64748b;
  margin: 0;
  text-align: center;
  line-height: 1.3;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProofGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 40px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const ProofPoint = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  text-align: left;
`;

const ProofTitle = styled('h3')`
  font-size: 15px;
  font-weight: 700;
  color: ${INK};
  margin: 0 0 4px;
`;

const ProofBody = styled('p')`
  font-size: 13.5px;
  line-height: 1.55;
  color: #64748b;
  margin: 0;
`;

const WallFootnote = styled('p')`
  text-align: center;
  font-size: 14px;
  line-height: 1.6;
  color: #64748b;
  max-width: 560px;
  margin: 40px auto 0;

  a {
    color: ${COLORS.PRIMARY};
    font-weight: 600;
    text-decoration: none;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const WarehouseSection = styled('section')`
  max-width: 1100px;
  margin: 88px auto 0;
  padding: 0 24px;

  @media (max-width: 800px) {
    margin-top: 64px;
  }
`;

const WarehouseHeader = styled('div')`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 40px;
`;

const WarehouseIntro = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #64748b;
  margin: 12px 0 0;
`;

const WarehouseGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  max-width: 860px;
  margin: 0 auto;

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const WarehouseTile = styled('div')`
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 96px;
  padding: 18px 12px 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(14, 28, 43, 0.04);
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;

  img {
    max-height: 28px;
    max-width: 70%;
    object-fit: contain;
  }

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(46, 92, 255, 0.1);
    transform: translateY(-2px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const WarehouseLink = styled('div')`
  text-align: center;
  margin-top: 32px;
`;

const LayerSection = styled('section')`
  max-width: 1100px;
  margin: 88px auto;
  padding: 0 24px;

  @media (max-width: 800px) {
    margin: 64px auto;
  }
`;

const LayerHeader = styled('div')`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 40px;
`;

const LayerIntro = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #64748b;
  margin: 12px 0 0;
`;

const LayerGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const LayerCard = styled('div')`
  height: 100%;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.2s;

  &:hover {
    border-color: ${COLORS.PRIMARY};
    box-shadow: 0 8px 30px rgba(46, 92, 255, 0.1);
    transform: translateY(-2px);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const LayerIcon = styled('div')`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #eef3ff;
  color: ${COLORS.PRIMARY};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LayerTitle = styled('h3')`
  font-size: 17px;
  font-weight: 700;
  color: ${INK};
  margin: 0;
`;

const LayerBody = styled('p')`
  font-size: 14px;
  line-height: 1.6;
  color: #64748b;
  margin: 0;
  flex: 1;
`;

const InlineLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${COLORS.PRIMARY};
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;

  &:hover {
    opacity: 0.8;
  }
`;

const RequestSection = styled('section')`
  max-width: 720px;
  margin: 0 auto 120px;
  padding: 0 24px;
`;

const RequestHeader = styled('div')`
  text-align: center;
  margin-bottom: 32px;
`;

const RequestIntro = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: #64748b;
  margin: 12px 0 0;
`;

const RequestCard = styled('div')`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(14, 28, 43, 0.04);

  @media (max-width: 600px) {
    padding: 24px 20px;
  }
`;

type PageQueryData = {
  allSanityAffiliateNetwork: {
    edges: {
      node: Integration;
    }[];
  };
};

// Amazon and Impact logos must never render on the site (see POSITIONING.md).
const HIDDEN_INTEGRATIONS = ['amazon', 'impact'];

const isHiddenIntegration = (i: Integration) =>
  HIDDEN_INTEGRATIONS.some(
    (hidden) =>
      i.slug.current.indexOf(hidden) !== -1 ||
      i.name.toLowerCase().indexOf(hidden) !== -1
  );

const MOSAIC_TILE_COUNT = 24;
const MOSAIC_SWAP_INTERVAL_MS = 1200;
const MOSAIC_FADE_MS = 550;

/** These networks are always on the wall and never rotate out. */
const PINNED_INTEGRATIONS = [
  'awin',
  'cj-affiliate',
  'skimlinks',
  'linkby',
  'levanta',
  'avantlink',
  'webgains'
];

/**
 * Fixed grid slots for the pinned networks, spread across the mosaic.
 * All are below 18 so they stay visible on small screens, where the
 * mosaic is trimmed to the first 18 tiles.
 */
const PINNED_SLOTS = [0, 4, 7, 10, 13, 15, 17];

const SWAPPABLE_SLOTS = Array.from(
  { length: MOSAIC_TILE_COUNT },
  (_, slot) => slot
).filter((slot) => PINNED_SLOTS.indexOf(slot) === -1);

const buildInitialMosaic = (integrations: Integration[]) => {
  const pinnedBySlot = new Map<number, Integration>();
  PINNED_INTEGRATIONS.forEach((slug, index) => {
    const match = integrations.find((i) => i.slug.current === slug);
    if (match) {
      pinnedBySlot.set(PINNED_SLOTS[index], match);
    }
  });
  const others = integrations.filter(
    (i) => PINNED_INTEGRATIONS.indexOf(i.slug.current) === -1
  );

  const slots: Integration[] = [];
  let otherIndex = 0;
  for (let slot = 0; slot < MOSAIC_TILE_COUNT; slot++) {
    const pinned = pinnedBySlot.get(slot);
    slots.push(pinned || others[otherIndex++]);
  }
  return { slots, pool: others.slice(otherIndex) };
};

/**
 * One mosaic cell. When its integration changes, the outgoing logo
 * fades out on top of the incoming one for a smooth crossfade.
 */
const MosaicTile = ({ integration }: { integration: Integration }) => {
  const [layers, setLayers] = useState<{
    current: Integration;
    previous: Integration | null;
  }>({ current: integration, previous: null });

  useEffect(() => {
    setLayers((prev) =>
      prev.current.slug.current === integration.slug.current
        ? prev
        : { current: integration, previous: prev.current }
    );
    const timeout = setTimeout(
      () =>
        setLayers((prev) =>
          prev.previous ? { current: prev.current, previous: null } : prev
        ),
      MOSAIC_FADE_MS
    );
    return () => clearTimeout(timeout);
  }, [integration]);

  const renderLayer = (item: Integration, className?: string) => (
    <MosaicLayer key={item.slug.current} className={className}>
      <LogoImg
        src={item.logo.image.src}
        alt={`${item.name} Integration`}
        loading="lazy"
      />
      <LogoName>{item.name}</LogoName>
    </MosaicLayer>
  );

  return (
    <MosaicTileFrame>
      {layers.previous && renderLayer(layers.previous, 'out')}
      {renderLayer(layers.current, layers.previous ? 'in' : undefined)}
    </MosaicTileFrame>
  );
};

/**
 * Fixed-size mosaic that continuously swaps in logos from the full
 * integration pool, one random tile at a time. Rotation pauses when
 * offscreen and is disabled entirely under prefers-reduced-motion.
 */
const IntegrationMosaic = ({
  integrations
}: {
  integrations: Integration[];
}) => {
  const reduced = usePrefersReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>({
    threshold: 0.15,
    once: false
  });
  const initialRef = useRef(buildInitialMosaic(integrations));
  const [visible, setVisible] = useState(initialRef.current.slots);
  const poolRef = useRef(initialRef.current.pool);
  const lastTileRef = useRef(-1);
  const preloadedRef = useRef(false);

  // Preload offscreen logos once the mosaic is first seen, so swaps
  // never show a half-loaded image.
  useEffect(() => {
    if (!inView || preloadedRef.current) {
      return;
    }
    preloadedRef.current = true;
    poolRef.current.forEach((i) => {
      const img = new Image();
      img.src = i.logo.image.src;
    });
  }, [inView]);

  useEffect(() => {
    if (reduced || !inView || poolRef.current.length === 0) {
      return;
    }
    const interval = setInterval(() => {
      setVisible((current) => {
        const pool = poolRef.current;
        let slotPosition = Math.floor(Math.random() * SWAPPABLE_SLOTS.length);
        if (SWAPPABLE_SLOTS[slotPosition] === lastTileRef.current) {
          slotPosition = (slotPosition + 1) % SWAPPABLE_SLOTS.length;
        }
        const tileIndex = SWAPPABLE_SLOTS[slotPosition];
        lastTileRef.current = tileIndex;
        const poolIndex = Math.floor(Math.random() * pool.length);
        const incoming = pool[poolIndex];
        pool[poolIndex] = current[tileIndex];
        const next = current.slice();
        next[tileIndex] = incoming;
        return next;
      });
    }, MOSAIC_SWAP_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [reduced, inView]);

  return (
    <MosaicGrid ref={ref}>
      {visible.map((integration, index) => (
        <MosaicTile key={index} integration={integration} />
      ))}
    </MosaicGrid>
  );
};

const PROOF_POINTS = [
  {
    icon: Globe,
    title: 'Verified by the world\u2019s biggest publishers',
    body:
      'Countless data checks and reconciliations have been run by publishers on Affilimate, so you know the data is accurate and reliable.'
  },
  {
    icon: Check,
    title: 'Continuously updated during the transaction lifecycle',
    body:
      'Transactions are normalized and updated as networks confirm and pay, so reports reflect what you actually earn.'
  },
  {
    icon: Database,
    title: 'More than transactions',
    body:
      "Transactions, partnerships, rates, and more sync into the same unified record with a well-designed data model that's been tested at scale."
  }
] as const;

/** Logos live in website/static/images/data-destinations/. */
const DATA_DESTINATIONS = [
  { name: 'BigQuery', logo: '/images/data-destinations/bigquery.svg' },
  { name: 'Snowflake', logo: '/images/data-destinations/snowflake.svg' },
  { name: 'Redshift', logo: '/images/data-destinations/redshift.svg' },
  { name: 'Power BI', logo: '/images/data-destinations/power-bi.svg' },
  { name: 'Looker', logo: '/images/data-destinations/looker.svg' },
  { name: 'Tableau', logo: '/images/data-destinations/tableau.svg' },
  { name: 'Databricks', logo: '/images/data-destinations/databricks.svg' },
  { name: 'Metabase', logo: '/images/data-destinations/metabase.svg' }
] as const;

const LAYER_CARDS = [
  {
    icon: BarChart3,
    title: 'Reports your team can trust',
    body:
      'Transactions, rates, and payouts from your networks, normalized into a consistent structure. Hundreds of publisher requests have already been integrated to reach this robust state.',
    linkText: 'Explore Unify',
    linkTo: '/product/unify/'
  },
  {
    icon: Sparkles,
    title: 'Answers directly from your network data',
    body:
      'The Affilimate Agent answers sponsorship, earnings, and page questions from the same unified dataset, with links back to live reports.',
    linkText: 'Explore the AI Platform',
    linkTo: '/ai-platform/'
  },
  {
    icon: Target,
    title: 'Campaigns performance consistent with the networks',
    body:
      "Flight sponsorship campaigns and track placements with performance data that lines up across networks and partners, so you're all on the same page.",
    linkText: 'Explore Amplify',
    linkTo: '/product/amplify/'
  }
] as const;

export default ({
  data: {
    allSanityAffiliateNetwork: { edges }
  }
}: {
  data: PageQueryData;
}) => {
  const visibleIntegrations = edges
    .map((e) => e.node)
    .filter((i) => i.status === 'supported')
    .filter((i) => !isHiddenIntegration(i));

  return (
    <MainLayout>
      <div>
        <SEO
          pathname="/integrations/"
          title="Integrations with Affiliate Networks"
          description="100+ affiliate networks, subnetworks, direct programs, and aggregators feeding one unified data layer: the foundation Affilimate's analytics, Agent, and campaigns run on."
        />
        <NetworksSection>
          <NetworksInner>
            <IntegrationsHero>
              <Headline
                variant="h1"
                gutterBottom
                component="h1"
                style={{
                  maxWidth: '800px',
                  margin: '0 auto 16px',
                  fontWeight: 900,
                  display: 'block'
                }}
              >
                100+ integrations. One data layer.
              </Headline>
              <Typography
                variant="body1"
                color="textSecondary"
                style={{ maxWidth: '620px', margin: '0 auto' }}
              >
                Affilimate integrates with over one hundred affiliate
                networks, subnetworks, seller networks, direct programs,
                aggregators, and SaaS platforms. Here's a few popular examples.
              </Typography>
              <div style={{ textAlign: 'center', margin: '28px auto 0' }}>
                <DemoButton text="Get a demo" variant="contained" />
              </div>
            </IntegrationsHero>
            <NetworksHeader>
              <SyncBadge>Integrated with 100+ sources</SyncBadge>
            </NetworksHeader>
            <IntegrationMosaic integrations={visibleIntegrations} />
            <ProofGrid>
              {PROOF_POINTS.map((point, index) => {
                const Icon = point.icon;
                return (
                  <Reveal key={point.title} delay={index * 80}>
                    <ProofPoint>
                      <ProductIcon $size="xs">
                        <Icon size={18} />
                      </ProductIcon>
                      <div>
                        <ProofTitle>{point.title}</ProofTitle>
                        <ProofBody>{point.body}</ProofBody>
                      </div>
                    </ProofPoint>
                  </Reveal>
                );
              })}
            </ProofGrid>
            <Reveal>
              <WallFootnote>
                Wondering about a specific integration?{' '}
                <a href="#integration-request">Get in touch.</a>
              </WallFootnote>
            </Reveal>
          </NetworksInner>
        </NetworksSection>

        <WarehouseSection>
          <Reveal>
            <WarehouseHeader>
              <Headline component="h2" style={{ margin: 0 }}>
                Sync with the data tools your team already uses
              </Headline>
              <WarehouseIntro>
                Push unified commerce data into your warehouse and open it in
                your BI tools, so finance and analytics can join it with the ad,
                audience, and subscriber signals they already model.
              </WarehouseIntro>
            </WarehouseHeader>
          </Reveal>
          <WarehouseGrid>
            {DATA_DESTINATIONS.map((destination, index) => (
              <Reveal
                key={destination.name}
                delay={(index % 4) * 60}
                distance={14}
                style={{ height: '100%' }}
              >
                <WarehouseTile>
                  <img
                    src={destination.logo}
                    alt={`${destination.name} logo`}
                  />
                  <LogoName>{destination.name}</LogoName>
                </WarehouseTile>
              </Reveal>
            ))}
          </WarehouseGrid>
          <Reveal>
            <WarehouseLink>
              <InlineLink to="/product/unify/">
                See warehouse exports on Unify <ArrowRight size={16} />
              </InlineLink>
            </WarehouseLink>
          </Reveal>
        </WarehouseSection>

        <LayerSection>
          <Reveal>
            <LayerHeader>
              <Headline component="h2" style={{ margin: 0 }}>
                Connect once, use everywhere
              </Headline>
              <LayerIntro>
                Each integration feeds your unified commerce data, so UI
                reports, AI tools, campaigns, and more work from identical
                numbers.
              </LayerIntro>
            </LayerHeader>
          </Reveal>
          <LayerGrid>
            {LAYER_CARDS.map((card, index) => {
              const Icon = card.icon;
              return (
                <Reveal
                  key={card.title}
                  delay={index * 60}
                  style={{ height: '100%' }}
                >
                  <LayerCard>
                    <LayerIcon>
                      <Icon size={20} />
                    </LayerIcon>
                    <LayerTitle>{card.title}</LayerTitle>
                    <LayerBody>{card.body}</LayerBody>
                    <InlineLink to={card.linkTo}>
                      {card.linkText} <ArrowRight size={16} />
                    </InlineLink>
                  </LayerCard>
                </Reveal>
              );
            })}
          </LayerGrid>
        </LayerSection>

        <RequestSection id="integration-request">
          <Reveal>
            <RequestHeader>
              <Headline component="h2" style={{ margin: 0 }}>
                Looking for a specific integration?
              </Headline>
              <RequestIntro>
                Affilimate integrates with over 100 data sources. Get in touch
                with us about the integration you want to know about, and
                we&apos;ll let you know what support looks like.
              </RequestIntro>
            </RequestHeader>
          </Reveal>
          <Reveal delay={120}>
            <RequestCard>
              <ContactForm />
            </RequestCard>
          </Reveal>
        </RequestSection>
      </div>
    </MainLayout>
  );
};

export const pageQuery = graphql`
  query MyQuery {
    allSanityAffiliateNetwork(
      filter: { status: { eq: "supported" } }
      sort: { order: ASC, fields: name }
    ) {
      edges {
        node {
          name
          status
          slug {
            current
          }
          logo {
            image {
              src
            }
          }
        }
      }
    }
  }
`;
